/**
 * GlassPanel - T042
 *
 * Base glass morphism container with Spatial Glass UI
 */
import { type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';

interface GlassPanelProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  children: ReactNode;
  variant?: 'default' | 'subtle' | 'elevated';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

const variants = {
  default: 'bg-white/[0.02] border border-white/[0.05]',
  subtle: 'bg-white/[0.015] border border-white/[0.03]',
  elevated: 'bg-white/[0.04] border border-white/[0.07] shadow-lg shadow-black/20',
};

const paddings = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export function GlassPanel({
  children,
  variant = 'default',
  padding = 'md',
  hover = false,
  className = '',
  ...props
}: GlassPanelProps) {
  const baseClasses = `rounded-xl backdrop-blur-lg ${variants[variant]} ${paddings[padding]}`;
  const hoverClasses = hover ? 'transition-colors hover:bg-white/[0.05] hover:border-white/[0.08]' : '';

  return (
    <motion.div
      className={`${baseClasses} ${hoverClasses} ${className}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export default GlassPanel;
